import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { FaUserTie, FaTruck, FaFileInvoiceDollar, FaDollarSign } from 'react-icons/fa';
import { MdLocalShipping } from 'react-icons/md';
import Paper from 'components/Paper';
import { LoadingPage } from 'components/Loading';
import Button from 'components/Button';
import { fretesAPI, viagensAPI, cargasAPI } from 'services/apiRest';
import { Container, Content, HeaderActions, StatsGrid, StatCard, StatValue, StatLabel, StatIcon, ChartsGrid, ChartCard, ChartTitle, ListCard, ListItem, EmptyMessage } from './styles';

export default function EmbarcadoresDashboard() {
  const history = useHistory();
  const { empresa } = useSelector((state) => state.auth);

  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalFretes: 0,
    fretesAguardandoCotacao: 0,
    fretesAguardandoAprovacao: 0,
    fretesBuscandoMotorista: 0,
    fretesAguardandoCarregamento: 0,
    fretesEmitindoDocumentos: 0,
    fretesViagemIniciada: 0,
    fretesViagemEncerrada: 0,
    fretesCancelados: 0,
    totalViagens: 0,
    viagensPendentes: 0,
    viagensEmAndamento: 0,
    viagensConcluidas: 0,
    totalCargas: 0,
    valorTotalFretes: 0,
    valorTotalViagens: 0,
  });

  const [fretesRecentes, setFretesRecentes] = useState([]);
  const [statusFretes, setStatusFretes] = useState([]);
  const [statusViagens, setStatusViagens] = useState([]);

  useEffect(() => {
    carregarDados();
  }, [empresa?.id]);

  const carregarDados = async () => {
    try {
      setLoading(true);

      // Buscar fretes
      const fretes = await fretesAPI.listar({ empresa_id: empresa?.id });
      
      // Buscar viagens relacionadas a fretes (verificar pela note)
      const todasViagens = await viagensAPI.listar({ empresa_id: empresa?.id });
      const viagensDeFretes = todasViagens.filter(v => 
        v.note && v.note.includes('[FRETE_ID:')
      );

      // Buscar cargas relacionadas às viagens
      const todasCargas = await cargasAPI.listar({ empresa_id: empresa?.id });
      const cargasDeViagens = todasCargas.filter(c => {
        const viagemRelacionada = viagensDeFretes.find(v => v.id === c.viagem_id);
        return viagemRelacionada;
      });

      // Calcular estatísticas de fretes
      const totalFretes = fretes.length;
      const fretesAguardandoCotacao = fretes.filter(f => f.status === 'aguardando_cotacao' || f.status === null).length;
      const fretesAguardandoAprovacao = fretes.filter(f => f.status === 'aguardando_aprovacao').length;
      const fretesBuscandoMotorista = fretes.filter(f => f.status === 'buscando_motorista').length;
      const fretesAguardandoCarregamento = fretes.filter(f => f.status === 'aguardando_carregamento').length;
      const fretesEmitindoDocumentos = fretes.filter(f => f.status === 'emitindo_documentos').length;
      const fretesViagemIniciada = fretes.filter(f => f.status === 'viagem_iniciada').length;
      const fretesViagemEncerrada = fretes.filter(f => f.status === 'viagem_encerrada').length;
      const fretesCancelados = fretes.filter(f => f.status === 'cancelado').length;

      // Calcular estatísticas de viagens
      const totalViagens = viagensDeFretes.length;
      const viagensPendentes = viagensDeFretes.filter(v => v.status === 'pendente' || v.status === 'initial').length;
      const viagensEmAndamento = viagensDeFretes.filter(v => 
        v.status === 'Enviado' || v.status === 'Transmitido' || v.status === 'Autorizado'
      ).length;
      const viagensConcluidas = viagensDeFretes.filter(v => 
        v.status === 'Cancelado' || v.status === 'Rejeitado'
      ).length;

      // Calcular estatísticas de cargas
      const totalCargas = cargasDeViagens.length;

      // Calcular valores
      const valorTotalFretes = fretes.reduce((sum, f) => sum + (parseFloat(f.valor_frete) || 0), 0);
      const valorTotalViagens = viagensDeFretes.reduce((sum, v) => sum + (parseFloat(v.valor_receber) || 0), 0);

      // Preparar dados para gráficos de status
      const statusFretesData = [
        { label: 'Aguardando Cotação', value: fretesAguardandoCotacao, color: '#FFB703' },
        { label: 'Aguardando Aprovação', value: fretesAguardandoAprovacao, color: '#FF9800' },
        { label: 'Buscando Motorista', value: fretesBuscandoMotorista, color: '#2196F3' },
        { label: 'Aguardando Carregamento', value: fretesAguardandoCarregamento, color: '#9C27B0' },
        { label: 'Emitindo Documentos', value: fretesEmitindoDocumentos, color: '#673AB7' },
        { label: 'Viagem Iniciada', value: fretesViagemIniciada, color: '#4CAF50' },
        { label: 'Viagem Encerrada', value: fretesViagemEncerrada, color: '#8BC34A' },
        { label: 'Cancelados', value: fretesCancelados, color: '#F44336' },
      ].filter(item => item.value > 0);

      const statusViagensData = [
        { label: 'Pendentes', value: viagensPendentes, color: '#FFB703' },
        { label: 'Em Andamento', value: viagensEmAndamento, color: '#2196F3' },
        { label: 'Concluídas', value: viagensConcluidas, color: '#4CAF50' },
      ].filter(item => item.value > 0);

      // Fretes recentes (últimos 5)
      const fretesOrdenados = [...fretes].sort((a, b) => 
        new Date(b.created_at) - new Date(a.created_at)
      );
      const recentes = fretesOrdenados.slice(0, 5);

      setStats({
        totalFretes,
        fretesAguardandoCotacao,
        fretesAguardandoAprovacao,
        fretesBuscandoMotorista,
        fretesAguardandoCarregamento,
        fretesEmitindoDocumentos,
        fretesViagemIniciada,
        fretesViagemEncerrada,
        fretesCancelados,
        totalViagens,
        viagensPendentes,
        viagensEmAndamento,
        viagensConcluidas,
        totalCargas,
        valorTotalFretes,
        valorTotalViagens,
      });

      setFretesRecentes(recentes);
      setStatusFretes(statusFretesData);
      setStatusViagens(statusViagensData);
    } catch (error) {
      console.error('Erro ao carregar dados do dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatarMoeda = (valor) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(valor || 0);
  };

  const formatarStatus = (status) => {
    const statusMap = {
      null: 'Aguardando Cotação',
      aguardando_cotacao: 'Aguardando Cotação',
      aguardando_aprovacao: 'Aguardando Aprovação',
      buscando_motorista: 'Buscando Motorista',
      aguardando_carregamento: 'Aguardando Carregamento',
      emitindo_documentos: 'Emitindo Documentos',
      viagem_iniciada: 'Viagem Iniciada',
      viagem_encerrada: 'Viagem Encerrada',
      cancelado: 'Cancelado',
    };
    return statusMap[status] || status || 'Sem Status';
  };

  const getStatusColor = (status) => {
    const colorMap = {
      null: '#FFB703',
      aguardando_cotacao: '#FFB703',
      aguardando_aprovacao: '#FF9800',
      buscando_motorista: '#2196F3',
      aguardando_carregamento: '#9C27B0',
      emitindo_documentos: '#673AB7',
      viagem_iniciada: '#4CAF50',
      viagem_encerrada: '#8BC34A',
      cancelado: '#F44336',
    };
    return colorMap[status] || '#757575';
  };

  if (loading) {
    return (
      <Container>
        <Paper title="DASHBOARD EMBARCADORES">
          <Content>
            <LoadingPage />
          </Content>
        </Paper>
      </Container>
    );
  }

  return (
    <Container>
      <Paper
        title="DASHBOARD EMBARCADORES"
        icon={<FaUserTie size={24} style={{ color: '#122d58', marginRight: '25px' }} />}
        rightContent={
          <HeaderActions>
            <Button
              label="Novo Frete"
              onClick={() => history.push('/embarcadores/fretes/new')}
              type="button"
            />
          </HeaderActions>
        }
      >
        <Content>
          {/* Cards de Estatísticas */}
          <StatsGrid>
            <StatCard>
              <StatIcon style={{ background: '#FFB703' }}>
                <FaFileInvoiceDollar size={24} color="#fff" />
              </StatIcon>
              <div>
                <StatValue>{stats.totalFretes}</StatValue>
                <StatLabel>Total de Fretes</StatLabel>
              </div>
            </StatCard>

            <StatCard>
              <StatIcon style={{ background: '#2196F3' }}>
                <MdLocalShipping size={24} color="#fff" />
              </StatIcon>
              <div>
                <StatValue>{stats.totalViagens}</StatValue>
                <StatLabel>Viagens Geradas</StatLabel>
              </div>
            </StatCard>

            <StatCard>
              <StatIcon style={{ background: '#4CAF50' }}>
                <FaTruck size={24} color="#fff" />
              </StatIcon>
              <div>
                <StatValue>{stats.totalCargas}</StatValue>
                <StatLabel>Cargas</StatLabel>
              </div>
            </StatCard>

            <StatCard>
              <StatIcon style={{ background: '#8BC34A' }}>
                <FaDollarSign size={24} color="#fff" />
              </StatIcon>
              <div>
                <StatValue>{formatarMoeda(stats.valorTotalFretes)}</StatValue>
                <StatLabel>Valor Total Fretes</StatLabel>
              </div>
            </StatCard>
          </StatsGrid>

          {/* Gráficos e Listas */}
          <ChartsGrid>
            {/* Status dos Fretes */}
            <ChartCard>
              <ChartTitle>Status dos Fretes</ChartTitle>
              {statusFretes.length > 0 ? (
                <div style={{ padding: '20px' }}>
                  {statusFretes.map((item, index) => (
                    <div
                      key={index}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        marginBottom: '15px',
                        padding: '10px',
                        background: '#f5f5f5',
                        borderRadius: '5px',
                      }}
                    >
                      <div
                        style={{
                          width: '20px',
                          height: '20px',
                          background: item.color,
                          borderRadius: '3px',
                          marginRight: '10px',
                        }}
                      />
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: '14px', fontWeight: 600, color: '#333' }}>
                          {item.label}
                        </div>
                      </div>
                      <div style={{ fontSize: '18px', fontWeight: 700, color: '#122d58' }}>
                        {item.value}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <EmptyMessage>Nenhum frete encontrado</EmptyMessage>
              )}
            </ChartCard>

            {/* Status das Viagens */}
            <ChartCard>
              <ChartTitle>Status das Viagens</ChartTitle>
              {statusViagens.length > 0 ? (
                <div style={{ padding: '20px' }}>
                  {statusViagens.map((item, index) => (
                    <div
                      key={index}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        marginBottom: '15px',
                        padding: '10px',
                        background: '#f5f5f5',
                        borderRadius: '5px',
                      }}
                    >
                      <div
                        style={{
                          width: '20px',
                          height: '20px',
                          background: item.color,
                          borderRadius: '3px',
                          marginRight: '10px',
                        }}
                      />
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: '14px', fontWeight: 600, color: '#333' }}>
                          {item.label}
                        </div>
                      </div>
                      <div style={{ fontSize: '18px', fontWeight: 700, color: '#122d58' }}>
                        {item.value}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <EmptyMessage>Nenhuma viagem encontrada</EmptyMessage>
              )}
            </ChartCard>

            {/* Fretes Recentes */}
            <ListCard>
              <ChartTitle>Fretes Recentes</ChartTitle>
              {fretesRecentes.length > 0 ? (
                <div style={{ padding: '10px' }}>
                  {fretesRecentes.map((frete) => (
                    <ListItem
                      key={frete.id}
                      onClick={() => history.push(`/embarcadores/fretes/view/${frete.id}`)}
                    >
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: '14px', fontWeight: 600, color: '#333' }}>
                          Frete #{frete.numero || frete.id.substring(0, 8)}
                        </div>
                        <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                          {formatarStatus(frete.status)}
                        </div>
                        {frete.valor_frete && (
                          <div style={{ fontSize: '12px', color: '#4CAF50', marginTop: '4px', fontWeight: 600 }}>
                            {formatarMoeda(frete.valor_frete)}
                          </div>
                        )}
                      </div>
                      <div
                        style={{
                          width: '12px',
                          height: '12px',
                          background: getStatusColor(frete.status),
                          borderRadius: '50%',
                          marginLeft: '10px',
                        }}
                      />
                    </ListItem>
                  ))}
                </div>
              ) : (
                <EmptyMessage>Nenhum frete recente</EmptyMessage>
              )}
            </ListCard>
          </ChartsGrid>
        </Content>
      </Paper>
    </Container>
  );
}
